import { isEmpty, isNaN } from 'lodash';

import { ValidationMessages } from '../constants/appConstants';
import { getVendorNameLength } from './common';
import {
  isViewPointErp,
  isVendorLengthExempt,
  isGlDateInOpenPeriod,
  formatGlMonth
} from './erpValidationConfig';

/**
 * Spell out the company's open AP window on every Vista GL Date message, so the user
 * is told which months they CAN post to rather than only that this one is wrong.
 * No-ops when the ERP company record hasn't synced and the window is unknown.
 */
const withGlPeriodRange = (message, range) => {
  const first = formatGlMonth(range?.firstOpenMonth);
  const last = formatGlMonth(range?.lastOpenMonth);
  if (!first || !last) return message;
  return `${message}. ${ValidationMessages.VISTA_GL_OPEN_PERIODS} ${first} to ${last}`;
};

/**
 * Vista only: a GL Date that is well-formed (1st of a month) must still land inside
 * the company's open AP period window, derived from the synced ERP company record
 * (see getVistaOpenGlMonthRange). Returns '' when the date is fine or when no range
 * was supplied — a missing/unsynced company record must never block the field.
 */
const getVistaGlPeriodError = (value, range) => {
  if (isGlDateInOpenPeriod(value, range)) return '';
  return withGlPeriodRange(ValidationMessages.VISTA_GL_DATE_CLOSED_PERIOD, range);
};

const validateTotal = (value) => {
  const regex = /^-?[\d\s,\.]+$/;
  return regex.test(value);
};
const validateAmount = (value) => {
  //cannot include negative
  const regex = /^[\d\s,\.]+$/;
  return regex.test(value);
};
const validateDiscount = (value, isVista = false) => {
  // VP Vista: the discount field holds a flat amount only — "%" is NOT allowed — but it
  // may be negative (a surcharge/adjustment), so it gets an optional leading minus, and a
  // "$" currency symbol is accepted (stripped on ERP export). Every other ERP allows "%"
  // (converted to an amount on ERP export) and keeps digits positive.
  const regex = isVista ? /^-?[\d\s,\.$]+$/ : /^[\d\s,\.%]+$/; // digits, spaces, commas, dots (+ "$" on Vista, "%" off-Vista)
  return regex.test(value);
};
const validateDate = (datum, mustBeFirstDay = false) => {
  const cleanedDate = datum?.toString()?.replace(/(\d+)(st|nd|rd|th)/, '$1');
  const twoDigitYearRegex = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2})$/;
  if (twoDigitYearRegex.test(cleanedDate)) {
    return false;
  }
  let parsedDateString = cleanedDate;

  // Fix for Safari: convert MM-DD-YYYY or MM/DD/YYYY → YYYY-MM-DD
  const mdyMatch = cleanedDate?.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (mdyMatch) {
    const [, mm, dd, yyyy] = mdyMatch;
    parsedDateString = `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }

  const newDate = new Date(parsedDateString);

  if (isNaN(newDate.getTime())) {
    return false;
  }

  // Use UTC methods to avoid timezone shift issues.
  const day = newDate.getUTCDate();
  const month = newDate.getUTCMonth() + 1;
  const year = newDate.getUTCFullYear();

  if (!day || !month || !year) {
    return false;
  }
  if (mustBeFirstDay && day !== 1) {
    return false;
  }
  const containsFullDate =
    /^(?:(?:\b\w+\b[ ,]*(?:\b\d{1,2}(?:st|nd|rd|th)?\b)[ ,]*\b\d{4}\b)|(?:\b\d{1,2}\b[\/\-.]\b\d{1,2}\b[\/\-.]\b\d{4}\b))$/i;

  return containsFullDate.test(datum);
};

export const ValidateAnnotation = (value, name, paid, paymentType, options = {}) => {
  const erpType = sessionStorage.getItem('erpType');
  const vendorNamelength = getVendorNameLength(erpType);
  let discountValue = sessionStorage.getItem('discountValue') || '';
  let discountDateValue = sessionStorage.getItem('discountDateValue') || '';

  let res = { status: true, message: '' };
  if (name === 'total') {
    if (validateTotal(value) && value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;

      res['message'] = ValidationMessages.INVALID_AMOUNT;
    }
  }

  if (name === 'invoice_date') {
    if (value?.trim('') === '') {
      res['status'] = false;
      res['message'] = ValidationMessages.EMPTY_INVOICE_DATE;
    } else if (validateDate(value)) {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_DATE;
    }
  }

  if (name === 'due_date') {
    const trimmedDueDate = value == null ? '' : String(value).trim();
    const isEmptyDueDate = trimmedDueDate === '' || trimmedDueDate === '-';
    if (isEmptyDueDate) {
      res['status'] = true;
    } else if (!validateDate(value)) {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_DUE_DATE;
    } else if (isViewPointErp(erpType)) {
      const parsedDate = new Date(value);
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      if (parsedDate < oneYearAgo) {
        res['status'] = false;
        res['message'] = ValidationMessages.VISTA_DUE_DATE_TOO_OLD;
      } else {
        res['status'] = true;
      }
    } else {
      res['status'] = true;
    }
  }
  if (name === 'transaction_date') {
    const isVista = isViewPointErp(erpType);
    const glDateRange = options?.vistaGlDateRange;
    if (value?.trim('') === '') {
      res['status'] = false;
      res['message'] = isVista
        ? withGlPeriodRange(ValidationMessages.VISTA_EMPTY_TRANSACTION_DATE, glDateRange)
        : ValidationMessages.EMPTY_TRANSACTION_DATE;
    } else if (validateDate(value, isVista)) {
      // Vista: a well-formed GL Date must also sit in an open AP period.
      const periodError = isVista ? getVistaGlPeriodError(value, glDateRange) : '';
      if (periodError) {
        res['status'] = false;
        res['message'] = periodError;
      } else {
        res['status'] = true;
      }
    } else {
      res['status'] = false;
      res['message'] = isVista
        ? withGlPeriodRange(ValidationMessages.INVALID_VISTA_TRANSACTION_DATE, glDateRange)
        : ValidationMessages.INVALID_TRANSACTION_DATE;
    }
  }

  if (name === 'discount_date') {
    discountDateValue = value;
    sessionStorage.setItem('discountDateValue', discountDateValue);

    if (!isEmpty(discountValue) && isEmpty(value)) {
      res['status'] = false;
      res['message'] = ValidationMessages.DISCOUNT_DATE_REQUIRED;
    } else if (isEmpty(value)) {
      res['status'] = true;
      res['message'] = '';
    } else if (validateDate(value)) {
      res['status'] = true;
      res['message'] = '';
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_DISCOUNT_DATE;
    }
  }

  if (name === 'discount') {
    discountValue = value;
    sessionStorage.setItem('discountValue', discountValue);

    if (!isEmpty(discountDateValue) && isEmpty(value)) {
      res['status'] = false;
      res['message'] = ValidationMessages.DISCOUNT_REQUIRED;
    } else if (isEmpty(value)) {
      res['status'] = true;
      res['message'] = '';
    } else if (validateDiscount(value, isViewPointErp(erpType))) {
      res['status'] = true;
      res['message'] = '';
    } else {
      res['status'] = false;
      // Vista's discount is an amount only (no "%"), so its message must not mention "%".
      res['message'] = isViewPointErp(erpType)
        ? ValidationMessages.INVALID_DISCOUNT_VISTA
        : ValidationMessages.INVALID_DISCOUNT;
    }
  }

  if (name === 'vendor') {
    if (!isEmpty(value) && value !== '-') {
      if (isVendorLengthExempt(erpType)) {
        res['status'] = true;
      } else {
        if (value?.length <= vendorNamelength) {
          res['status'] = true;
        } else {
          res['status'] = false;
          res['message'] = ValidationMessages.INVALID_VENDOR_LENGTH;
        }
      }
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_VENDOR;
    }
  }

  if (name === 'invoice_number') {
    // Vista permits a longer invoice number (30) than the default limit (20).
    const isVista = isViewPointErp(erpType);
    const maxInvoiceLength = isVista ? 30 : 20;
    if (!isEmpty(value) && value !== '-' && value?.length <= maxInvoiceLength) {
      res['status'] = true;
    } else {
      res['status'] = false;
      if (value?.length > maxInvoiceLength) {
        res['message'] = isVista
          ? ValidationMessages.INVALID_VISTA_INVOICELENGTH
          : ValidationMessages.INVALID_INVOICELENGTH;
      } else {
        res['message'] = ValidationMessages.INVALID_INVOICENUMBER;
      }
    }
  }
  if (paid && name === 'payment_type') {
    if (value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_PAYMENT_TYPE;
    }
  }
  if (paid && name === 'payment_amount') {
    if (validateAmount(value) && value?.trim('') !== '' && value?.length <= 10) {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_PAYMENT_AMOUNT;
    }
  }
  if (paid && name === 'check_no' && paymentType === 'CHK') {
    if (value?.trim('') !== '' && value?.length <= 10) {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_CHECKNO;
    }
  }
  if (paid && name === 'payment_date') {
    if (value?.trim('') === '') {
      res['status'] = false;
      res['message'] = ValidationMessages.EMPTY_PAYMENT_DATE;
    } else if (validateDate(value)) {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_PAYMENT_DATE;
    }
  }
  if (name === 'notes') {
    if (value?.length > 30) {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_FOUNDATION_DESCRIPTION_LENGTH;
    } else {
      res['status'] = true;
      res['message'] = '';
    }
  }
  // Retainage on an invoice. A subcontractor's progress billing arrives as an
  // ordinary invoice and still has retainage held against it — the field used to
  // be a DOLLAR box (`retainage_amount`) that a customer typed "10" into meaning
  // ten percent, and whose value reached no ERP at all. It is the rate now, read
  // by exactly the rule the pay application uses. Blank is valid here: see
  // validateRetainagePercentage.
  if (name === 'retainage_percentage') {
    res = validateRetainagePercentage(value, { allowBlank: true });
  }

  // Current Payment Due is total minus retainage — derived, read-only, and
  // present on a Foundation invoice for the same reason it is on a pay
  // application. There is no way for a reviewer to make it invalid, and
  // flagging it would point them at a box they cannot edit instead of at the
  // Total that produced it. `validateG702` answers the same way.
  //
  // It also has to be said out loud rather than left to the default: the box is
  // injected BLANK on every invoice extracted before the field existed, and an
  // empty amount is exactly what the other total rules refuse.
  if (name === 'current_payment_due') {
    res = { status: true, message: '' };
  }
  return res;
};

export const ValidateReceipt = (value, name, paid, paymentType, options = {}) => {
  const erpType = sessionStorage.getItem('erpType');
  const vendorNamelength = getVendorNameLength(erpType);
  let res = { status: true, message: '' };
  let discountValue = sessionStorage.getItem('discountValue') || '';
  let discountDateValue = sessionStorage.getItem('discountDateValue') || '';

  if (name === 'total') {
    if (validateTotal(value) && value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_AMOUNT;
    }
  }
  if (name === 'receipt_date') {
    if (validateDate(value) && value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_RECEIPT_DATE;
    }
  }
  if (name === 'due_date') {
    if (!validateDate(value)) {
      res['status'] = true;
    } else {
      if (isViewPointErp(erpType)) {
        const parsedDate = new Date(value);
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        if (parsedDate < oneYearAgo) {
          res['status'] = false;
          res['message'] = ValidationMessages.VISTA_DUE_DATE_TOO_OLD;
        } else {
          res['status'] = true;
        }
      } else {
        res['status'] = true;
      }
    }
  }
  if (name === 'transaction_date') {
    const isVista = isViewPointErp(erpType);
    const glDateRange = options?.vistaGlDateRange;
    if (value?.trim('') === '') {
      res['status'] = false;
      res['message'] = isVista
        ? withGlPeriodRange(ValidationMessages.VISTA_EMPTY_TRANSACTION_DATE, glDateRange)
        : ValidationMessages.EMPTY_TRANSACTION_DATE;
    } else if (validateDate(value, isVista)) {
      // Vista: a well-formed GL Date must also sit in an open AP period.
      const periodError = isVista ? getVistaGlPeriodError(value, glDateRange) : '';
      if (periodError) {
        res['status'] = false;
        res['message'] = periodError;
      } else {
        res['status'] = true;
      }
    } else {
      res['status'] = false;
      res['message'] = isVista
        ? withGlPeriodRange(ValidationMessages.INVALID_VISTA_TRANSACTION_DATE, glDateRange)
        : ValidationMessages.INVALID_TRANSACTION_DATE;
    }
  }

  if (name === 'vendor') {
    if (!isEmpty(value) && value !== '-') {
      if (isVendorLengthExempt(erpType)) {
        res['status'] = true;
      } else {
        if (value?.length <= vendorNamelength) {
          res['status'] = true;
        } else {
          res['status'] = false;
          res['message'] = ValidationMessages.INVALID_VENDOR_LENGTH;
        }
      }
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_VENDOR;
    }
  }

  if (name === 'receipt_number') {
    if (!isEmpty(value) && value !== '-' && value?.length <= 20) {
      res['status'] = true;
    } else {
      res['status'] = false;
      if (value?.length > 20) {
        res['message'] = ValidationMessages.INVALID_RECEIPTLENGTH;
      } else {
        res['message'] = ValidationMessages.INVALID_RECEIPTNUMBER;
      }
    }
  }
  if (name === 'discount_date') {
    discountDateValue = value;
    sessionStorage.setItem('discountDateValue', discountDateValue);

    if (!isEmpty(discountValue) && isEmpty(value)) {
      res['status'] = false;
      res['message'] = ValidationMessages.DISCOUNT_DATE_REQUIRED;
    } else if (isEmpty(value)) {
      res['status'] = true;
      res['message'] = '';
    } else if (validateDate(value)) {
      res['status'] = true;
      res['message'] = '';
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_DISCOUNT_DATE;
    }
  }

  if (name === 'discount') {
    discountValue = value;
    sessionStorage.setItem('discountValue', discountValue);

    if (!isEmpty(discountDateValue) && isEmpty(value)) {
      res['status'] = false;
      res['message'] = ValidationMessages.DISCOUNT_REQUIRED;
    } else if (isEmpty(value)) {
      res['status'] = true;
      res['message'] = '';
    } else if (validateDiscount(value, isViewPointErp(erpType))) {
      res['status'] = true;
      res['message'] = '';
    } else {
      res['status'] = false;
      // Vista's discount is an amount only (no "%"), so its message must not mention "%".
      res['message'] = isViewPointErp(erpType)
        ? ValidationMessages.INVALID_DISCOUNT_VISTA
        : ValidationMessages.INVALID_DISCOUNT;
    }
  }
  if (paid && name === 'payment_type') {
    if (value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_PAYMENT_TYPE;
    }
  }
  if (paid && name === 'payment_amount') {
    if (validateAmount(value) && value?.trim('') !== '' && value?.length <= 10) {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_PAYMENT_AMOUNT;
    }
  }
  if (paid && name === 'check_no' && paymentType === 'CHK') {
    if (value?.trim('') !== '' && value?.length <= 10) {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_CHECKNO;
    }
  }
  if (paid && name === 'payment_date') {
    if (value?.trim('') === '') {
      res['status'] = false;
      res['message'] = ValidationMessages.EMPTY_PAYMENT_DATE;
    } else if (validateDate(value)) {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_PAYMENT_DATE;
    }
  }
  if (name === 'notes') {
    if (value?.length > 30) {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_FOUNDATION_DESCRIPTION_LENGTH;
    } else {
      res['status'] = true;
      res['message'] = '';
    }
  }
  return res;
};

export const ValidateEstimate = (value, name, annotationType) => {
  const erpType = sessionStorage.getItem('erpType');
  const vendorNamelength = getVendorNameLength(erpType);
  let res = { status: true, message: '' };

  if (name === 'total') {
    if (validateTotal(value) && value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_AMOUNT;
    }
  }
  if (name === 'Estimate_date' || name === 'Po_date') {
    if (validateDate(value) && value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] =
        name === 'Po_date'
          ? ValidationMessages.INVALID_PO_DATE
          : ValidationMessages.INVALID_ESTIMATE_DATE;
    }
  }

  if (name === 'Expiration_date') {
    if (isEmpty(value)) {
      res['status'] = true;
    } else {
      if (validateDate(value)) {
        res['status'] = true;
      } else {
        res['status'] = false;
        res['message'] = ValidationMessages.INVALID_EXPIRATION_DATE;
      }
    }
  }

  if (name === 'vendor') {
    if (!isEmpty(value) && value !== '-') {
      if (isVendorLengthExempt(erpType)) {
        res['status'] = true;
      } else {
        if (value?.length <= vendorNamelength) {
          res['status'] = true;
        } else {
          res['status'] = false;
          res['message'] = ValidationMessages.INVALID_VENDOR_LENGTH;
        }
      }
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_VENDOR;
    }
  }

  if (name === 'Estimate_no' && annotationType === 'Estimate') {
    if (!isEmpty(value) && value !== '-' && value?.length <= 20) {
      res['status'] = true;
    } else {
      res['status'] = false;
      if (value?.length > 20) {
        res['message'] = ValidationMessages.INVALID_ESTIMATELENGTH;
      } else {
        res['message'] = ValidationMessages.INVALID_ESTIMATENUMBER;
      }
    }
  }
  if (name === 'purchase_order' && annotationType === 'Po') {
    if (!isEmpty(value) && value !== '-' && value?.length <= 10) {
      res['status'] = true;
    } else {
      res['status'] = false;
      if (value?.length > 10) {
        res['message'] = ValidationMessages.INVALID_POLENGTH;
      } else {
        res['message'] = ValidationMessages.INVALID_PONUMBER;
      }
    }
  }

  // Vista caps the header description (`notes`) at 30 characters — hard check.
  if (name === 'notes' && isViewPointErp(erpType) && value?.length > 30) {
    res['status'] = false;
    res['message'] = ValidationMessages.INVALID_FOUNDATION_DESCRIPTION_LENGTH;
  }

  return res;
};
export const ValidateDeliveryTicket = (value, name) => {
  const erpType = sessionStorage.getItem('erpType');
  const vendorNamelength = getVendorNameLength(erpType);
  let res = { status: true, message: '' };

  if (name === 'DeliveryTicket_date') {
    if (validateDate(value) && value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_DT_DATE;
    }
  }

  if (name === 'vendor') {
    if (!isEmpty(value) && value !== '-') {
      if (isVendorLengthExempt(erpType)) {
        res['status'] = true;
      } else {
        if (value?.length <= vendorNamelength) {
          res['status'] = true;
        } else {
          res['status'] = false;
          res['message'] = ValidationMessages.INVALID_VENDOR_LENGTH;
        }
      }
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_VENDOR;
    }
  }

  if (name === 'DeliveryTicket_no') {
    if (!isEmpty(value) && value !== '-' && value?.length <= 20) {
      res['status'] = true;
    } else {
      res['status'] = false;
      if (value?.length > 20) {
        res['message'] = ValidationMessages.INVALID_DELIVERYLENGTH;
      } else {
        res['message'] = ValidationMessages.INVALID_DELIVERYNUMBER;
      }
    }
  }

  return res;
};
export const autoCap = (value) => {
  const spiltVal = value?.split('_');
  for (let i = 0; i <= spiltVal?.length; i++) {
    if (spiltVal[i] === undefined) {
      continue;
    } else {
      spiltVal[i] = spiltVal[i][0]?.toUpperCase() + spiltVal[i]?.substr(1);
    }
  }
  return spiltVal?.join(' ');
};
export function replaceContentNames(name, erpType) {
  const isVista = erpType === 'viewPointVista';

  const replacements = {
    Estimate_date: 'Quote Date',
    Estimate_no: 'Quote No',
    DeliveryTicket_date: 'Document Date',
    DeliveryTicket_no: 'Document No',
    purchase_order: 'PO/ Subcontract',
    payment_status: 'Paid',
    notes: 'Description',
    transaction_date: isVista ? 'GL Date' : 'Transaction Date',
    miscellaneous_amount: 'Misc Amount/Freight',
    tax_code: 'Tax Code',
    tax_type: 'Tax Type',
    // Vista's pay control travels under the BE key `accountant` — only the label differs.
    accountant: 'Pay Control'
  };

  return replacements[name] || autoCap(name);
}

// The retainage RATE, shared by the pay application and the invoice — the two
// document types that withhold. One rule, because they withhold identically:
// `resolveRetainageAmounts` on the backend takes this percentage and the total
// and computes the amount, and a rate this screen accepted but that rule cannot
// read is retainage the ERP silently does not hold.
//
// A percentage, and bounded. validateAmount alone accepts "12,345" and "100",
// and both of those reached the export writer: AIA line 5 prints the rate and
// the retainage DOLLAR amount on the same line ("10 % of Completed Work
// $12,345"), so extraction picks up the money about as often as the rate — and a
// dollar amount in this box computes retainage of 12,345% of the billing. Catch
// it where the person can see the form, not at export.
//
// `allowBlank` is the one difference. On a pay application retainage is the
// point and a blank is a form nobody finished reading. On an INVOICE the field
// is new and most invoices carry no retainage at all: it is injected blank on
// every invoice extracted before it existed, and the export writes an empty
// column for a blank rather than withholding zero — so failing a blank would
// block approval of documents that were fine yesterday. It also decides the
// wording: the pay application's message says the box must not be empty, and
// repeating that on an invoice would send the reviewer looking for a number the
// document does not have.
export const validateRetainagePercentage = (value, { allowBlank = false } = {}) => {
  const entered = String(value ?? '').trim();
  if (allowBlank && (entered === '' || entered === '-')) {
    return { status: true, message: '' };
  }
  const percent = Number(entered.replace(/[%$,\s]/g, ''));
  if (!validateAmount(value) || entered === '') {
    return {
      status: false,
      message: allowBlank
        ? ValidationMessages.INVALID_RETAINAGE_OPTIONAL
        : ValidationMessages.INVALID_RETAINAGE
    };
  }
  if (!Number.isFinite(percent) || percent < 0 || percent >= 100) {
    return { status: false, message: ValidationMessages.INVALID_RETAINAGE_RANGE };
  }
  return { status: true, message: '' };
};

export const validateG702 = (value, name, options) => {
  const erpType = sessionStorage.getItem('erpType');
  const fromContractorlength = getVendorNameLength(erpType);
  let res = { status: true, message: '' };
  // The total is the one captured amount on a pay application: retainage is
  // withheld from it, the line items are matched against it, and the FSI export
  // reports it as the header amount. So it is the amount that has to be valid.
  if (name === 'total') {
    if (validateTotal(value) && value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_G702_TOTAL;
    }
  }

  // Current Payment Due is total minus retainage, derived and read-only — there
  // is no way for a user to make it invalid, and flagging it would point them at
  // a field they cannot edit instead of at the Total that produced it.
  if (name === 'current_payment_due') {
    res['status'] = true;
  }

  if (name === 'from_contractor') {
    if (!isEmpty(value) && value !== '-' && value?.trim('') !== '') {
      if (isVendorLengthExempt(erpType)) {
        res['status'] = true;
      } else {
        if (value?.length <= fromContractorlength) {
          res['status'] = true;
        } else {
          res['status'] = false;
          res['message'] = ValidationMessages.INVALID_VENDOR_LENGTH;
        }
      }
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_VENDOR;
    }
  }

  if (name === 'retainage_percentage') {
    res = validateRetainagePercentage(value);
  }
  if (name === 'period_to') {
    if (validateDate(value) && value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      res['message'] = ValidationMessages.INVALID_G702_DATE;
    }
  }
  // The GL posting date, which a pay application carries now like every invoice.
  //
  // Validated only when there is something in it. The field is NEW on this
  // document type, so it is injected blank on every pay application extracted
  // before it existed, and both ERPs that read it fall back to the period/invoice
  // date when it is empty — a blank is a legitimate state, and failing it would
  // block approval of documents that were fine yesterday. A malformed one is not
  // legitimate: it reaches the Foundation FSI Transaction Date column verbatim,
  // and on Vista it becomes the batch's GL month.
  if (name === 'transaction_date') {
    const isVista = isViewPointErp(erpType);
    const glDateRange = options?.vistaGlDateRange;
    const entered = String(value ?? '').trim();
    if (entered === '' || entered === '-') {
      res['status'] = true;
    } else if (validateDate(value, isVista)) {
      const periodError = isVista ? getVistaGlPeriodError(value, glDateRange) : '';
      if (periodError) {
        res['status'] = false;
        res['message'] = periodError;
      } else {
        res['status'] = true;
      }
    } else {
      res['status'] = false;
      res['message'] = isVista
        ? withGlPeriodRange(ValidationMessages.INVALID_VISTA_TRANSACTION_DATE, glDateRange)
        : ValidationMessages.INVALID_TRANSACTION_DATE;
    }
  }
  if (name === 'application_no') {
    if (!isEmpty(value) && value !== '-' && value?.length <= 20 && value?.trim('') !== '') {
      res['status'] = true;
    } else {
      res['status'] = false;
      if (value?.length > 20) {
        res['message'] = ValidationMessages.INVALID_APPLICATION_LENGTH;
      } else {
        res['message'] = ValidationMessages.INVALID_PROJECTNUMBER;
      }
    }
  }

  return res;
};

export const isEmailValid = (email) => {
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return emailRegex.test(email);
};

export function compareObjects(obj1, obj2) {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1?.length !== keys2?.length) {
    return false;
  }

  const resultObject = {};
  for (let key of keys1) {
    if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
      // Compare array elements
      if (obj1[key]?.length !== obj2[key]?.length) {
        resultObject[key] = false;
      } else {
        let equal = true;
        for (let i = 0; i < obj1[key]?.length; i++) {
          if (obj1[key][i] !== obj2[key][i]) {
            equal = false;
            break;
          }
        }
        resultObject[key] = equal;
      }
    } else {
      resultObject[key] = obj1[key] === obj2[key];
    }
  }

  return resultObject;
}

// A content-field value counts as "set" when it's a non-empty array (multi-select)
// or a non-empty, non-placeholder string.
const hasContentValue = (val) =>
  Array.isArray(val)
    ? val?.length > 0
    : val !== undefined &&
      val !== null &&
      String(val)?.trim() !== '' &&
      String(val)?.trim() !== '-';

export const validateData = (value, name, annotationType, paid, paymentType, options = {}) => {
  // Reviewer Group is required once a Reviewer is entered. This is one-way only: a Reviewer
  // Group can be submitted on its own (with or without a Reviewer), so a Reviewer is never
  // required by this rule.
  if (name === 'reviewer' || name === 'reviewer_group') {
    if (
      name === 'reviewer_group' &&
      hasContentValue(options?.reviewerValue) &&
      !hasContentValue(value)
    ) {
      return { status: false, message: 'Reviewer Group is required' };
    }
    return { status: true, message: '' };
  }

  // "Export Date as Transaction Date": the field is read-only and its value is
  // replaced by the export date on the way out, so a stale extracted value must not
  // block approval. The caller decides whether the flag applies to this document
  // type (canvasUtils.usesExportDateAsTransactionDate — never a G702).
  if (name === 'transaction_date' && options?.exportDateAsTransactionDate) {
    return { status: true, message: '' };
  }

  let validationResponse = { status: true, message: '' };
  switch (annotationType) {
    case 'G702':
      validationResponse = validateG702(value, name, options);
      break;
    case 'Receipt':
      validationResponse = ValidateReceipt(value, name, paid, paymentType, options);
      break;
    case 'Estimate':
    case 'Po':
      validationResponse = ValidateEstimate(value, name, annotationType);
      break;
    case 'DeliveryTicket':
      validationResponse = ValidateDeliveryTicket(value, name);
      break;
    default:
      validationResponse = ValidateAnnotation(value, name, paid, paymentType, options);
  }

  return validationResponse;
};

export const isValidPage = (page) => {
  return page !== null && page !== undefined && page !== '' && page !== '-';
};

export const convertKeyToLabel = (key, erpType) => {
  // Foundation / VP Vista surface Cost Id as "Cost Code", QuickBooks Desktop as "Item" —
  // BE keys (userCostId / costId) are unchanged, only the displayed label differs.
  const costIdLabel =
    erpType === 'Foundation' || erpType === 'viewPointVista' || erpType === 'Acumatica'
      ? 'Cost Code'
      : erpType === 'QuickBooksDesktop'
      ? 'Item'
      : 'Cost Id';
  // Acumatica's own words for job / phase / cost type (Project / Task / Account Group).
  const isAcumatica = erpType === 'Acumatica';
  const jobLabel = isAcumatica ? 'Project' : 'Job Id';
  const phaseLabel = isAcumatica ? 'Task' : 'Phases';
  const costTypeLabel = isAcumatica ? 'Account Group' : 'Cost Type';
  const knownMappings = {
    userCostId: costIdLabel,
    costId: costIdLabel,
    userJobId: jobLabel,
    jobId: jobLabel,
    userPhases: phaseLabel,
    phases: phaseLabel,
    userCostType: costTypeLabel,
    costType: costTypeLabel,
    glAccount: 'Gl Account',
    subaccount: 'Subaccount',
    equipmentNo: 'Equipment No',
    serviceCode: 'Service Code',
    glDivision: 'Gl Division',
    paymentStatus: 'Payment Status',
    paymentType: 'Payment Type',
    checkNo: 'Check No',
    paymentAmount: 'Payment Amount',
    invoiceNumber: 'Invoice Number',
    payment_status: 'Payment Status',
    invoice_number: 'Invoice Number',
    workOrder: 'Work Order',
    scope: 'Scope'
  };

  const normalizedKey = key?.replace(/_/g, '')?.toLowerCase();

  for (const mapKey in knownMappings) {
    if (normalizedKey === mapKey?.replace(/_/g, '')?.toLowerCase()) {
      return knownMappings[mapKey];
    }
  }

  return key
    ?.replace(/_/g, ' ') // handle snake_case
    ?.replace(/([a-z])([A-Z])/g, '$1 $2') // handle camelCase
    ?.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // handle ALLCAPS with title case
    ?.replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize each word
};
